import { useEffect, useState } from 'react';
import { managerAPI, salesTargetsAPI, teamAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users, DollarSign, TrendingUp, Target, Phone, Calendar, Mail,
  CheckSquare, TicketIcon, BarChart3, PieChart, Activity, Award,
  ArrowUp, ArrowDown, Minus, RefreshCw, Plus, Loader2
} from 'lucide-react';

export const ManagerDashboardPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [period, setPeriod] = useState('month');
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Target dialog
  const [targetDialog, setTargetDialog] = useState(false);
  const [targetForm, setTargetForm] = useState({
    user_id: '',
    period_type: 'monthly',
    period_start: '',
    period_end: '',
    revenue_target: 0,
    deals_target: 0,
    calls_target: 0,
    meetings_target: 0,
    emails_target: 0
  });

  useEffect(() => {
    fetchDashboard();
    fetchTeamMembers();
  }, [period]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await managerAPI.getDashboard(period);
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await teamAPI.getUsers();
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const handleCreateTarget = async (e) => {
    e.preventDefault();
    try {
      await salesTargetsAPI.create({
        ...targetForm,
        period_start: new Date(targetForm.period_start).toISOString(),
        period_end: new Date(targetForm.period_end).toISOString()
      });
      toast.success('Sales target created');
      setTargetDialog(false);
      setTargetForm({
        user_id: '',
        period_type: 'monthly',
        period_start: '',
        period_end: '',
        revenue_target: 0,
        deals_target: 0,
        calls_target: 0,
        meetings_target: 0,
        emails_target: 0
      });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create target');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getTrendIcon = (value, target) => {
    if (!target) return <Minus className="h-4 w-4 text-slate-400" />;
    const progress = (value / target) * 100;
    if (progress >= 100) return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (progress >= 75) return <ArrowUp className="h-4 w-4 text-blue-500" />;
    if (progress >= 50) return <Minus className="h-4 w-4 text-amber-500" />;
    return <ArrowDown className="h-4 w-4 text-red-500" />;
  };

  if (!hasRole(['admin', 'sales_manager', 'team_lead'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Target className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">
            Only managers and team leads can access this dashboard.
          </p>
        </Card>
      </div>
    );
  }

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const pipeline = dashboard?.pipeline || {};
  const teamPerformance = dashboard?.team_performance || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manager-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Manager Oversight Dashboard
          </h1>
          <p className="text-muted-foreground">
            Track team performance, revenue targets, and conversion rates
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchDashboard} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setTargetDialog(true)} data-testid="set-targets-btn">
            <Target className="h-4 w-4 mr-2" />
            Set Targets
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="h-4 w-4" />
              Total Revenue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-700" data-testid="total-revenue">
              {formatCurrency(summary.total_revenue)}
            </p>
            <p className="text-sm text-emerald-600 mt-1">
              {summary.total_deals_won} deals closed
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-blue-700">
              <TrendingUp className="h-4 w-4" />
              Conversion Rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-700" data-testid="conversion-rate">
              {summary.overall_conversion_rate}%
            </p>
            <p className="text-sm text-blue-600 mt-1">
              {summary.total_deals_won} won / {summary.total_deals_created} created
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-purple-700">
              <Activity className="h-4 w-4" />
              Team Activity
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-xl font-bold text-purple-700">{summary.total_calls}</p>
                <p className="text-xs text-purple-600">Calls</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-700">{summary.total_meetings}</p>
                <p className="text-xs text-purple-600">Meetings</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-purple-700">{summary.total_emails_sent}</p>
                <p className="text-xs text-purple-600">Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2 text-amber-700">
              <TicketIcon className="h-4 w-4" />
              Support Metrics
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-amber-700">
              {summary.open_tickets}
            </p>
            <p className="text-sm text-amber-600 mt-1">
              Open tickets · {summary.avg_resolution_hours}h avg resolution
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="team" className="space-y-4">
        <TabsList>
          <TabsTrigger value="team" data-testid="team-tab">Team Performance</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="pipeline-tab">Deal Pipeline</TabsTrigger>
          <TabsTrigger value="targets" data-testid="targets-tab">Targets & Goals</TabsTrigger>
        </TabsList>

        {/* Team Performance Tab */}
        <TabsContent value="team" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Performance
              </CardTitle>
              <CardDescription>
                Individual performance metrics for the {period === 'day' ? 'day' : period === 'week' ? 'week' : period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : 'year'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {teamPerformance.map((member) => (
                    <div
                      key={member.user_id}
                      className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`team-member-${member.user_id}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {member.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{member.name}</p>
                            <Badge variant="outline" className="text-xs">{member.role}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-emerald-600">
                            {formatCurrency(member.metrics.revenue)}
                          </p>
                          <p className="text-xs text-muted-foreground">Revenue</p>
                        </div>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.deals_won}</p>
                          <p className="text-xs text-muted-foreground">Deals Won</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.conversion_rate}%</p>
                          <p className="text-xs text-muted-foreground">Conv. Rate</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.calls}</p>
                          <p className="text-xs text-muted-foreground">Calls</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.meetings}</p>
                          <p className="text-xs text-muted-foreground">Meetings</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.emails_sent}</p>
                          <p className="text-xs text-muted-foreground">Emails</p>
                        </div>
                        <div className="text-center p-2 bg-slate-50 rounded">
                          <p className="text-lg font-semibold">{member.metrics.task_completion_rate}%</p>
                          <p className="text-xs text-muted-foreground">Tasks Done</p>
                        </div>
                      </div>

                      {/* Target Progress */}
                      {member.targets.revenue_target > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Revenue Target Progress</span>
                            <span className="font-medium flex items-center gap-1">
                              {getTrendIcon(member.metrics.revenue, member.targets.revenue_target)}
                              {member.targets.revenue_progress}%
                            </span>
                          </div>
                          <Progress 
                            value={Math.min(member.targets.revenue_progress, 100)} 
                            className={`h-2 ${getProgressColor(member.targets.revenue_progress)}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(member.metrics.revenue)} of {formatCurrency(member.targets.revenue_target)} target
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {teamPerformance.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-10 w-10 mx-auto mb-3 opacity-50" />
                      <p>No team members found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Deal Pipeline Overview
              </CardTitle>
              <CardDescription>
                Current deal distribution across all stages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {Object.entries(pipeline).map(([stage, data]) => {
                  const stageColors = {
                    prospecting: 'bg-slate-100 border-slate-300',
                    qualification: 'bg-blue-100 border-blue-300',
                    proposal: 'bg-purple-100 border-purple-300',
                    negotiation: 'bg-amber-100 border-amber-300',
                    closed_won: 'bg-emerald-100 border-emerald-300',
                    closed_lost: 'bg-red-100 border-red-300'
                  };
                  return (
                    <div
                      key={stage}
                      className={`p-4 rounded-lg border-2 ${stageColors[stage] || 'bg-slate-100 border-slate-300'}`}
                    >
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                        {stage.replace('_', ' ')}
                      </p>
                      <p className="text-2xl font-bold">{data.count}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(data.value)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Targets Tab */}
        <TabsContent value="targets" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Sales Targets & Goals
                  </CardTitle>
                  <CardDescription>
                    Set and track targets for team members
                  </CardDescription>
                </div>
                <Button onClick={() => setTargetDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Target
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamPerformance.filter(m => m.targets.revenue_target > 0).map((member) => (
                  <div key={member.user_id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>{member.name?.charAt(0) || 'U'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <Badge variant="outline">{member.role}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Revenue Target */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" /> Revenue
                          </span>
                          <span>{member.targets.revenue_progress}%</span>
                        </div>
                        <Progress value={Math.min(member.targets.revenue_progress, 100)} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(member.metrics.revenue)} / {formatCurrency(member.targets.revenue_target)}
                        </p>
                      </div>

                      {/* Deals Target */}
                      {member.targets.deals_target > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" /> Deals
                            </span>
                            <span>{member.targets.deals_progress}%</span>
                          </div>
                          <Progress value={Math.min(member.targets.deals_progress, 100)} className="h-2" />
                          <p className="text-xs text-muted-foreground">
                            {member.metrics.deals_won} / {member.targets.deals_target}
                          </p>
                        </div>
                      )}

                      {/* Calls Target */}
                      {member.targets.calls_target > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> Calls
                            </span>
                            <span>{member.targets.calls_progress}%</span>
                          </div>
                          <Progress value={Math.min(member.targets.calls_progress, 100)} className="h-2" />
                          <p className="text-xs text-muted-foreground">
                            {member.metrics.calls} / {member.targets.calls_target}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {teamPerformance.filter(m => m.targets.revenue_target > 0).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p>No targets set yet</p>
                    <p className="text-sm">Click "Add Target" to set goals for team members</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Set Target Dialog */}
      <Dialog open={targetDialog} onOpenChange={setTargetDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Sales Target</DialogTitle>
            <DialogDescription>
              Set performance targets for a team member
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTarget} className="space-y-4">
            <div className="space-y-2">
              <Label>Team Member</Label>
              <Select 
                value={targetForm.user_id} 
                onValueChange={(v) => setTargetForm(p => ({ ...p, user_id: v }))}
              >
                <SelectTrigger data-testid="target-user-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name || member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select 
                  value={targetForm.period_type}
                  onValueChange={(v) => setTargetForm(p => ({ ...p, period_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Revenue Target ($)</Label>
                <Input
                  type="number"
                  value={targetForm.revenue_target}
                  onChange={(e) => setTargetForm(p => ({ ...p, revenue_target: parseFloat(e.target.value) || 0 }))}
                  data-testid="revenue-target-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={targetForm.period_start}
                  onChange={(e) => setTargetForm(p => ({ ...p, period_start: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={targetForm.period_end}
                  onChange={(e) => setTargetForm(p => ({ ...p, period_end: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deals Target</Label>
                <Input
                  type="number"
                  value={targetForm.deals_target}
                  onChange={(e) => setTargetForm(p => ({ ...p, deals_target: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Calls Target</Label>
                <Input
                  type="number"
                  value={targetForm.calls_target}
                  onChange={(e) => setTargetForm(p => ({ ...p, calls_target: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTargetDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!targetForm.user_id} data-testid="create-target-btn">
                Create Target
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerDashboardPage;
